import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)
import { ProfileHeader, ProfileSection } from "@/components/sensnet/ProfileLayout"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  ArrowLeft,
  Package,
  AlertTriangle,
  Power,
  Trash2,
  Truck,
  Activity,
  Loader2,
  AlertCircle,
  ClipboardList,
  CheckCircle,
  QrCode,
  Image as ImageIcon,
} from "lucide-react"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { useDeviceProfileNetwork } from "@/hooks/useDeviceProfileNetwork"
import { useSecurityContext } from "@/contexts/SecurityContext"
import type {
  GetDeviceByNumberRegistry,
  GetDeviceTypeRegistry,
  GetDeviceManufacturerRegistry,
  GetDeviceDistributorRegistry,
} from "@generated-schemas"
import type { InferResponse } from "@/types/registryTypes"
import { matchesState, type DeviceDataForMatching } from "@/utils/deviceStateMatcher"
import { DEVICE_STATE_ACTIONS } from "./deviceStateActions"

// Updated DeviceStatus based on requirements
type DeviceStatus = 
  | "ordered" 
  | "shipped" 
  | "received" 
  | "testing" 
  | "tested"
  | "failed"
  | "available"
  | "live" 
  | "faulty" 
  | "returned" 
  | "retired"
  // Legacy/API statuses
  | "procured" 
  | "provisioned"

// Extended DeviceData to include new fields (mocking if not in schema yet)
// BaseDeviceData is the `content` sub-object from the wrapped API response
type BaseDeviceData = InferResponse<typeof GetDeviceByNumberRegistry>['content']
interface DeviceData {
  device: Omit<NonNullable<BaseDeviceData>['device'], 'deviceStatus'> & {
    deviceStatus: DeviceStatus
    manufacturerName?: string
    distributorName?: string
    devicePictureLocation?: string
    deviceQrCodeLocation?: string
  }
  procurementInfo?: NonNullable<BaseDeviceData>['procurementInfo']
  retirementInfo?: NonNullable<BaseDeviceData>['retirementInfo']
  faultInfo?: NonNullable<BaseDeviceData>['faultInfo']
  // Allow extra fields that may be joined/denormalized by the backend
  provisioningInfo?: NonNullable<BaseDeviceData>['provisioningInfo'] & {
    deviceProvisionedRequestedBy?: number
    networkCorridorName?: string
    networkSiteName?: string
  }
  provisioningInfos?: Array<
    NonNullable<NonNullable<BaseDeviceData>['provisioningInfo']> & {
      deviceProvisionedRequestedBy?: number
      networkCorridorName?: string
      networkSiteName?: string
    }
  >
}

type DeviceTypeData = InferResponse<typeof GetDeviceTypeRegistry>
type ManufacturerData = InferResponse<typeof GetDeviceManufacturerRegistry>
type DistributorData = InferResponse<typeof GetDeviceDistributorRegistry>

const statusConfig: Record<DeviceStatus, { className: string; label: string }> = {
  ordered: { className: "bg-trilogy-blue/15 text-trilogy-blue border-trilogy-blue/30", label: "Ordered" },
  shipped: { className: "bg-trilogy-yellow/15 text-trilogy-yellow border-trilogy-yellow/30", label: "Shipped" },
  received: { className: "bg-trilogy-yellow/15 text-trilogy-yellow border-trilogy-yellow/30", label: "Received" },
  testing: { className: "bg-trilogy-yellow/15 text-trilogy-yellow border-trilogy-yellow/30", label: "Testing" },
  tested: { className: "bg-trilogy-blue/15 text-trilogy-blue border-trilogy-blue/30", label: "Tested" },
  failed: { className: "bg-trilogy-red/15 text-trilogy-red border-trilogy-red/30", label: "Test Failed" },
  available: { className: "bg-trilogy-blue/15 text-trilogy-blue border-trilogy-blue/30", label: "Available For Provisioning" },
  live: { className: "bg-trilogy-blue/15 text-trilogy-blue border-trilogy-blue/30", label: "Live" },
  faulty: { className: "bg-trilogy-red/15 text-trilogy-red border-trilogy-red/30", label: "Faulty" },
  returned: { className: "bg-trilogy-red/15 text-trilogy-red border-trilogy-red/30", label: "Returned" },
  retired: { className: "bg-trilogy-gray/15 text-trilogy-gray border-trilogy-gray/30", label: "Retired" },
  // Mappings for API statuses
  procured: { className: "bg-trilogy-yellow/15 text-trilogy-yellow border-trilogy-yellow/30", label: "Procured" }, // Maps to Received/Available
  provisioned: { className: "bg-trilogy-blue/15 text-trilogy-blue border-trilogy-blue/30", label: "Live" }, // Maps to Live
}

interface DeviceDetailProps {
  deviceNumber: number
  onBack?: () => void
}

export function DeviceDetail({ deviceNumber, onBack }: DeviceDetailProps) {
  // Dialog State
  const [activeActionId, setActiveActionId] = useState<string | null>(null)
  const { userInfo } = useSecurityContext()

  // Fetch device detail from API. Provisioning success/failure is set
  // asynchronously by the backend Prov service, so poll while it's still
  // `requested` and stop once it resolves (or the device leaves the pending state).
  const { data: rawApiResponse, isLoading, error, refetch } = useApiQuery(
    "getDeviceByNumber",
    { deviceNumber },
    {
      refetchInterval: (arg: any) => {
        // v4 passes the data (TData); v5 passes the query object — handle both.
        const data = arg?.state?.data ?? arg
        const prov = (data as any)?.content?.provisioningInfo
        const status = Array.isArray(prov)
          ? prov[prov.length - 1]?.deviceProvisioningStatus
          : prov?.deviceProvisioningStatus
        return status === 'requested' ? 5000 : false
      },
    } as any,
  )

  // Extract the content from the wrapped response
  const rawDeviceData = rawApiResponse?.content

  // DEBUG: Log raw API response
  log.debug('Raw API response:', rawApiResponse);
  log.debug('provisioningInfo:', rawDeviceData?.provisioningInfo);
  log.debug('deviceProvisioningInfoNumber:', rawDeviceData?.device?.deviceProvisioningInfoNumber);

  // Cast to extended type
  const deviceData = rawDeviceData as unknown as DeviceData

  const deviceTypeNumber = deviceData?.procurementInfo?.deviceTypeNumber ?? (deviceData as any)?.device?.deviceTypeNumber
  const deviceDistributorNumber = deviceData?.procurementInfo?.deviceDistributorNumber

  // Fetch device type detail if available
  const { data: deviceTypeData } = useApiQuery<DeviceTypeData>(
    "getDeviceType",
    { deviceTypeNumber: deviceTypeNumber ?? 0 },
    { enabled: !!deviceTypeNumber }
  )

  const deviceTypeManufacturerNumber = deviceTypeData?.content?.deviceType?.deviceTypeManufacturerNumber

  // Fetch Manufacturer
  const { data: manufacturerData, isLoading: isManufacturerLoading } = useApiQuery<ManufacturerData>(
    "getDeviceManufacturer",
    { manufacturerNumber: deviceTypeManufacturerNumber ?? 0 },
    { enabled: !!deviceTypeManufacturerNumber }
  )

  // Fetch Distributor
  const { data: distributorData, isLoading: isDistributorLoading } = useApiQuery<DistributorData>(
    "getDeviceDistributor",
    { distributorNumber: deviceDistributorNumber ?? 0 },
    { enabled: !!deviceDistributorNumber }
  )

  const manufacturerName = manufacturerData?.content?.manufacturer?.manufacturerName
  const distributorName = distributorData?.content?.distributor?.distributorName

  // Resolve the device's Network Technology + Provisioning Required from its
  // Device Profile (network type lives on the profile; a device inherits it). The
  // backend device↔profile link is still pending (spec Feature 6), so this stays
  // unresolved until then — the network row hides, and provisioning defaults to
  // "required" (current behavior). See Features 2, 5.
  const { networkType, provisioningRequired } = useDeviceProfileNetwork(deviceData)
  const networkTechnology: string | null = networkType ?? null

  // Helper to display manufacturer. The backend returns null when none is set, so show
  // NOTHING in that case. Only "Loading..." while we're actually resolving a real number
  // (a disabled react-query reports isLoading=true, which previously stuck on "Loading...").
  const getManufacturerDisplay = () => {
    if (deviceTypeManufacturerNumber) {
      if (isManufacturerLoading) return "Loading..."
      return manufacturerName || `Manufacturer #${deviceTypeManufacturerNumber}`
    }
    // Device type is set but its manufacturer hasn't resolved yet → still loading the type.
    if (deviceTypeNumber && !deviceTypeData) return "Loading..."
    // Nothing selected → show nothing.
    return null
  }

  // Helper to display distributor. Null when none is selected → show nothing.
  const getDistributorDisplay = () => {
    // No distributor on this device — show nothing (the query is disabled, and in
    // react-query v4 a disabled query still reports isLoading=true, so guard on the
    // number to avoid a permanent "Loading..." for devices with no distributor).
    if (!deviceDistributorNumber) return null
    if (isDistributorLoading) return "Loading..."
    return distributorName || `Distributor #${deviceDistributorNumber}`
  }

  // Placeholder mutations (commented out as per original file)
  // const updateProcurementMutation = useApiMutation("upsertDeviceProcurementInfo", "POST")
  // const updateProvisioningMutation = useApiMutation("upsertDeviceProvisioningInfo", "POST")
  // const updateDeviceMutation = useApiMutation("upsertDevice", "POST")

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading device details...</span>
        </div>
      </div>
    )
  }

  if (error || !deviceData) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-lg font-medium">Failed to load device</p>
        <p className="text-sm text-muted-foreground">{error?.message || "Device not found"}</p>
        <div className="flex gap-2">
          {onBack ? (
            <Button variant="outline" onClick={onBack}>
              Back to Devices
            </Button>
          ) : (
            <Link to="/sensory/devices">
              <Button variant="outline">Back to Devices</Button>
            </Link>
          )}
          <Button onClick={() => refetch()}>Retry</Button>
        </div>
      </div>
    )
  }

  const { device, procurementInfo, provisioningInfo, faultInfo, retirementInfo } = deviceData

  // Mark as Faulty is reachable from: live, failed (test failure), or a device still
  // in `testing` whose provisioning failed/was deprovisioned (provisioningStatus, not
  // a deviceStatus). A single action condition can't express this OR, so gate here.
  const canMarkFaulty =
    device.deviceStatus === 'live' ||
    device.deviceStatus === 'failed' ||
    (device.deviceStatus === 'testing' &&
      (provisioningInfo?.deviceProvisioningStatus === 'failed' ||
        provisioningInfo?.deviceProvisioningStatus === 'deprovisioned'))

  type ProvisioningInfoRecord = NonNullable<DeviceData['provisioningInfo']>
  const rawProvisioningPayload = rawDeviceData as any
  const provisioningRecords: ProvisioningInfoRecord[] = (() => {
    const listFromPlural = Array.isArray(rawProvisioningPayload?.provisioningInfos)
      ? rawProvisioningPayload.provisioningInfos
      : Array.isArray(rawProvisioningPayload?.deviceProvisioningInfos)
        ? rawProvisioningPayload.deviceProvisioningInfos
        : []
    const listFromSingularArray = Array.isArray(rawProvisioningPayload?.provisioningInfo)
      ? rawProvisioningPayload.provisioningInfo
      : []
    const singleFromSingularObject =
      rawProvisioningPayload?.provisioningInfo && !Array.isArray(rawProvisioningPayload.provisioningInfo)
        ? [rawProvisioningPayload.provisioningInfo]
        : provisioningInfo
          ? [provisioningInfo]
          : []

    const combined = [...listFromPlural, ...listFromSingularArray, ...singleFromSingularObject].filter(Boolean)
    const dedupedByNumber = new Map<number | string, ProvisioningInfoRecord>()
    combined.forEach((item: ProvisioningInfoRecord) => {
      const key = item.deviceProvisioningInfoNumber ?? (item as any).id ?? `${item.deviceProvisioningType}-${item.deviceProvisionRequestedDate ?? 0}`
      dedupedByNumber.set(key, item)
    })
    return Array.from(dedupedByNumber.values())
  })()

  const pickLatestByType = (type: 'test' | 'live') => {
    const records = provisioningRecords.filter((item) => item?.deviceProvisioningType === type)
    if (records.length === 0) return undefined
    return records.reduce((latest, current) => {
      const latestTs = latest.deviceProvisionRequestedDate ?? 0
      const currentTs = current.deviceProvisionRequestedDate ?? 0
      return currentTs > latestTs ? current : latest
    })
  }

  const testProvisioningInfo = pickLatestByType('test')
  const liveProvisioningInfo = pickLatestByType('live')
  const faultRecord: any = (() => {
    const raw = rawDeviceData as any
    if (faultInfo) return faultInfo
    if (raw?.faultInfo && !Array.isArray(raw.faultInfo)) return raw.faultInfo
    if (Array.isArray(raw?.faultInfo) && raw.faultInfo.length > 0) return raw.faultInfo[raw.faultInfo.length - 1]
    if (Array.isArray(raw?.faultInfos) && raw.faultInfos.length > 0) return raw.faultInfos[raw.faultInfos.length - 1]
    return null
  })()
  const latestDeprovisionRecord = provisioningRecords
    .filter((item) => {
      const deprovisionDate =
        (item as any).deviceDeprovisionedDate ??
        (item as any).deviceDeprovisionDate ??
        0
      return deprovisionDate > 0
    })
    .reduce<ProvisioningInfoRecord | undefined>((latest, current) => {
      if (!latest) return current
      const latestTs =
        (latest as any).deviceDeprovisionedDate ??
        (latest as any).deviceDeprovisionDate ??
        0
      const currentTs =
        (current as any).deviceDeprovisionedDate ??
        (current as any).deviceDeprovisionDate ??
        0
      return currentTs > latestTs ? current : latest
    }, undefined)

  const retirementRequestedBy =
    (latestDeprovisionRecord as any)?.deviceDeprovisionedBy ??
    (liveProvisioningInfo as any)?.deviceProvisionedRequestedBy ??
    (testProvisioningInfo as any)?.deviceProvisionedRequestedBy ??
    0

  const status = statusConfig[device.deviceStatus] || statusConfig.retired
  const isCriticalLifecycle =
    device.deviceStatus === 'faulty' ||
    device.deviceStatus === 'returned' ||
    device.deviceStatus === 'retired'

  const formatDate = (timestamp?: number | null) => {
    if (!timestamp || timestamp === 0) return "—"
    // Backend returns seconds, Date expects milliseconds
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatNumber = (num?: number | null) => {
    if (!num || num === 0) return null
    return num
  }

  const formatRequestedBy = (requestedBy?: number) => {
    if (!requestedBy || requestedBy < 1000000000) return null
    if (userInfo?.userNumber === requestedBy) {
      const fullName = `${userInfo.userFirstName} ${userInfo.userLastName}`.trim()
      return fullName || String(requestedBy)
    }
    return String(requestedBy)
  }

  const formatAddress = (address?: {
    addressLine1?: string
    addressLine2?: string
    city?: string
    state?: string
    postalCode?: string
  }) => {
    if (!address) return null
    const parts: string[] = []
    
    // Add address line 1
    if (address.addressLine1) {
      parts.push(address.addressLine1)
    }
    
    // Add address line 2 if exists
    if (address.addressLine2) {
      parts.push(address.addressLine2)
    }
    
    // Add city, state, postal code
    const cityStateZip = [
      address.city,
      address.state,
      address.postalCode
    ].filter(Boolean).join(", ")
    
    if (cityStateZip) {
      parts.push(cityStateZip)
    }
    
    return parts.length > 0 ? parts.join("\n") : null
  }

  const formatCarrierName = (shipperName?: string) => {
    if (!shipperName) return null
    const carrierMap: Record<string, string> = {
      "fedex": "FedEx Express",
      "ups": "UPS",
      "usps": "USPS"
    }
    return carrierMap[shipperName.toLowerCase()] || shipperName.toUpperCase()
  }

  // Helper: Transform API response to state matcher format
  const toStateMatcherFormat = (data: DeviceData | null): DeviceDataForMatching | null => {
    if (!data) return null

    return {
      device: {
        deviceStatus: data.device.deviceStatus as any,
        deviceNumber: data.device.deviceNumber,
      },
      provisioningInfo: data.provisioningInfo ? {
        deviceProvisioningStatus: data.provisioningInfo.deviceProvisioningStatus as any,
        deviceProvisioningType: data.provisioningInfo.deviceProvisioningType as any,
        deviceProvisioningInfoNumber: data.provisioningInfo.deviceProvisioningInfoNumber ?? undefined,
      } : undefined,
    }
  }

  // Helper: Render action button based on device state
  const renderActionButton = (actionId: string) => {
    if (!deviceData) return null

    const action = DEVICE_STATE_ACTIONS.find(a => a.id === actionId)
    if (!action) return null

    // Transform data and check if this action matches current device state
    const stateData = toStateMatcherFormat(deviceData)
    if (!stateData || !matchesState(stateData, action.condition)) return null

    return (
      <Button
        size="sm"
        variant={action.variant || 'default'}
        onClick={() => setActiveActionId(actionId)}
      >
        {action.label}
      </Button>
    )
  }

  // Helper: Render dynamic action dialog
  const renderActionDialog = () => {
    if (!activeActionId || !deviceData) return null

    const action = DEVICE_STATE_ACTIONS.find(a => a.id === activeActionId)
    if (!action) return null

    const FormComponent = action.component
    const { device, procurementInfo, provisioningInfo } = deviceData

    return (
      <Dialog open={!!activeActionId} onOpenChange={(open) => !open && setActiveActionId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{action.label}</DialogTitle>
            {action.description && (
              <DialogDescription>{action.description}</DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="rounded-md bg-muted p-3 text-sm">
              <p><strong>Device:</strong> Device #{device.deviceNumber}</p>
              {/* A device is ordered from EITHER a manufacturer OR a distributor — show only the
                  one order source: distributor if it has one, otherwise the manufacturer. */}
              {getDistributorDisplay()
                ? <p><strong>Distributor:</strong> {getDistributorDisplay()}</p>
                : getManufacturerDisplay() && <p><strong>Manufacturer:</strong> {getManufacturerDisplay()}</p>}
            </div>
            <FormComponent
              deviceNumber={device.deviceNumber}
              deviceProcurementInfoNumber={procurementInfo?.deviceProcurementInfoNumber}
              deviceProvisioningInfoNumber={provisioningInfo?.deviceProvisioningInfoNumber}
              deviceDeliveryDate={procurementInfo?.deviceDeliveryDate}
              deviceName={device.deviceName ?? undefined}
              orderDate={procurementInfo?.deviceOrderDate}
              manufacturerName={getManufacturerDisplay()}
              distributorName={getDistributorDisplay()}
              networkCorridorName={provisioningInfo?.networkCorridorName}
              networkSiteName={provisioningInfo?.networkSiteName}
              previousNetworkCorridorNumber={provisioningInfo?.networkCorridorNumber}
              previousNetworkSiteNumber={provisioningInfo?.networkSiteNumber}
              // Cancelling a still-pending request reverts the device to 'received';
              // Deprovisioning a failed attempt leaves it in 'testing' (prov 'deprovisioned').
              revertToReceived={activeActionId === 'cancel-provision'}
              onCancel={() => setActiveActionId(null)}
              onSuccess={() => {
                setActiveActionId(null)
                refetch()
              }}
              showHeader={false}
            />
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // Timeline Event Component
  const TimelineEvent = ({
    title,
    date,
    comments,
    icon: Icon,
    isActive,
    isCompleted,
    actionButton
  }: {
    title: string,
    date?: number,
    comments?: React.ReactNode,
    icon: any,
    isActive?: boolean,
    isCompleted?: boolean,
    actionButton?: React.ReactNode
  }) => (
    <div className="relative pl-16 pb-10 last:pb-0 group animate-in fade-in slide-in-from-bottom-2 duration-500">
      {/* Vertical Line — full container height; icon sits on top */}
      <div className={`absolute left-[19px] top-0 bottom-0 w-[2px] ${
        isCompleted
          ? (isCriticalLifecycle ? "bg-trilogy-red/30" : "bg-primary/30")
          : "bg-border"
      } group-last:hidden`} />

      {/* Icon — rendered above the line via z-index with opaque background */}
      <div className={`absolute left-0 top-0 z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 shadow-sm transition-colors ${
        isCompleted
          ? (isCriticalLifecycle ? "border-trilogy-red bg-trilogy-red text-white" : "border-primary bg-primary text-primary-foreground")
          : isActive
            ? (isCriticalLifecycle ? "border-trilogy-red bg-background text-trilogy-red ring-4 ring-trilogy-red/10" : "border-primary bg-background text-primary ring-4 ring-primary/10")
            : "border-muted bg-background text-muted-foreground"
      }`}>
        <Icon className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className={`rounded-lg border p-4 transition-all ${
        isActive
          ? (isCriticalLifecycle ? "bg-card shadow-md border-trilogy-red/20" : "bg-card shadow-md border-primary/20")
          :
        isCompleted ? "bg-muted/10 border-transparent" :
        "bg-transparent border-transparent opacity-80"
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
          <h3 className={`font-semibold text-lg ${isActive || isCompleted ? "text-foreground" : "text-muted-foreground"}`}>
            {title}
          </h3>
          {date && (
            <Badge variant="secondary" className="w-fit font-normal text-xs">
              {formatDate(date)}
            </Badge>
          )}
        </div>

        {comments && (
          <div className="text-sm text-muted-foreground leading-relaxed mb-3">
            {comments}
          </div>
        )}

        {actionButton && (
          <div className="pt-1">
            {actionButton}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Back button row (matches Network Corridor profile) */}
      <div className="flex items-center justify-between">
        {onBack ? (
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-2 -ml-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        ) : (
          <Link to="/sensory/devices">
            <Button variant="ghost" size="sm" className="gap-2 -ml-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </Link>
        )}
      </div>

      {/* Profile Header (dark navy hero bar) */}
      <ProfileHeader
        title={device.deviceName?.trim() || `Device #${device.deviceNumber}`}
        subtitle={`Device Number: ${device.deviceNumber}`}
        subtitleIcon={<Package className="h-4 w-4" />}
        badge={status.label}
      />

      <div className="grid gap-6 md:grid-cols-3">
        {/* Main Column */}
        <div className="md:col-span-2 space-y-4">
          {/* Timeline Section */}
          <ProfileSection
            title="Device Lifecycle"
            icon={<Activity className="h-4 w-4" />}
            variant="dark"
            action={
              (['faulty', 'returned', 'retired', 'failed'] as string[]).includes(device.deviceStatus) ? (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-trilogy-red px-2.5 py-1 text-xs font-semibold text-white">
                  {device.deviceStatus === 'retired' ? (
                    <Trash2 className="h-3.5 w-3.5" />
                  ) : (
                    <AlertTriangle className="h-3.5 w-3.5" />
                  )}
                  {status.label}
                </span>
              ) : undefined
            }
          >
            <div className="p-5">
              <p className="text-sm text-muted-foreground mb-4">History of events and actions</p>
            <div className="mt-4">
              {/* Ordered */}
              <TimelineEvent
                title="Ordered"
                date={formatNumber(procurementInfo?.deviceOrderDate) || undefined}
                icon={ClipboardList}
                isCompleted={!!(formatNumber(procurementInfo?.deviceOrderDate))}
                comments={formatNumber(procurementInfo?.deviceOrderDate) && (
                  <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm mt-2">
                    <span className="text-muted-foreground">Order Date:</span>
                    <span>{formatDate(procurementInfo?.deviceOrderDate)}</span>
                    <span className="text-muted-foreground">PO Number:</span>
                    <span className="font-mono">{formatNumber(procurementInfo?.devicePurchaseOrderNumber) || 'N/A'}</span>
                  </div>
                )}
                actionButton={renderActionButton('mark-as-shipped')}
              />

              {/* Shipped */}
              <TimelineEvent
                title="Shipped"
                date={formatNumber(procurementInfo?.deviceShippingDate) || undefined}
                icon={Truck}
                isCompleted={!!(formatNumber(procurementInfo?.deviceShippingDate))}
                isActive={device.deviceStatus === 'shipped'}
                comments={formatNumber(procurementInfo?.deviceShippingDate) && (
                  <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm mt-2">
                    <span className="text-muted-foreground">Shipped Date:</span>
                    <span>{formatDate(procurementInfo?.deviceShippingDate)}</span>
                    <span className="text-muted-foreground">Courier:</span>
                    <span>{procurementInfo?.shipperName || '—'}</span>
                    <span className="text-muted-foreground">Tracking:</span>
                    <span>{procurementInfo?.deviceShippingTrackingNumber || '—'}</span>
                  </div>
                )}
                actionButton={renderActionButton('mark-as-received')}
              />

              {/* Received */}
              <TimelineEvent
                title="Received"
                date={formatNumber(procurementInfo?.deviceDeliveryDate) || undefined}
                icon={Package}
                isCompleted={!!(formatNumber(procurementInfo?.deviceDeliveryDate))}
                isActive={device.deviceStatus === 'received'}
                comments={formatNumber(procurementInfo?.deviceDeliveryDate) && (
                  <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm mt-2">
                    <span className="text-muted-foreground">Received Date:</span>
                    <span>{formatDate(procurementInfo?.deviceDeliveryDate)}</span>
                  </div>
                )}
                actionButton={
                  // Feature 5: skip provisioning when the device's profile doesn't
                  // require it (e.g. public 3G/4G/5G, already carrier-provisioned).
                  // Defaults to showing the action until the flag resolves.
                  provisioningRequired === false ? (
                    <p className="text-sm text-muted-foreground">
                      Provisioning not required for this device profile.
                    </p>
                  ) : (
                    renderActionButton('provision-for-testing')
                  )
                }
              />

              {/* Provisioned For Testing */}
              <TimelineEvent
                title={
                  device.deviceStatus === 'testing' && provisioningInfo?.deviceProvisioningStatus === 'failed'
                    ? "Provisioning Failed"
                    : "Provisioned For Testing"
                }
                date={
                  device.deviceStatus === 'testing'
                    ? formatNumber(provisioningInfo?.deviceProvisionRequestedDate) || undefined
                    : device.deviceStatus === 'tested' || device.deviceStatus === 'available' || device.deviceStatus === 'live'
                      ? formatNumber(provisioningInfo?.deviceProvisionRequestedDate) || undefined
                      : isCriticalLifecycle
                        ? formatNumber((testProvisioningInfo ?? provisioningInfo)?.deviceProvisionRequestedDate) || undefined
                        : undefined
                }
                icon={Power}
                isCompleted={
                  isCriticalLifecycle ||
                  device.deviceStatus === 'tested' ||
                  device.deviceStatus === 'available' ||
                  device.deviceStatus === 'live' ||
                  (
                    device.deviceStatus === 'testing' &&
                    provisioningInfo?.deviceProvisioningStatus === 'success'
                  )
                }
                isActive={device.deviceStatus === 'testing' && provisioningInfo?.deviceProvisioningStatus === 'requested'}
                comments={(device.deviceStatus === 'testing' || device.deviceStatus === 'tested' || device.deviceStatus === 'available' || device.deviceStatus === 'live' || isCriticalLifecycle) && (
                  <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm mt-2">
                    <span className="text-muted-foreground">Status:</span>
                    <Badge
                      variant={
                        device.deviceStatus === 'tested' || device.deviceStatus === 'available' || device.deviceStatus === 'live' || isCriticalLifecycle
                          ? 'default'
                          : device.deviceStatus === 'testing' && provisioningInfo?.deviceProvisioningStatus === 'failed'
                            ? 'destructive'
                            : 'secondary'
                      }
                      className="w-fit"
                    >
                      {device.deviceStatus === 'tested' || device.deviceStatus === 'available' || device.deviceStatus === 'live' || isCriticalLifecycle
                        ? 'Success'
                        : provisioningInfo?.deviceProvisioningStatus
                          ? provisioningInfo.deviceProvisioningStatus.charAt(0).toUpperCase() + provisioningInfo.deviceProvisioningStatus.slice(1)
                          : '—'}
                    </Badge>
                    <span className="text-muted-foreground">Network:</span>
                    <span>
                      {(isCriticalLifecycle ? (testProvisioningInfo ?? provisioningInfo) : provisioningInfo)?.networkCorridorName &&
                       (isCriticalLifecycle ? (testProvisioningInfo ?? provisioningInfo) : provisioningInfo)?.networkSiteName
                        ? `${(isCriticalLifecycle ? (testProvisioningInfo ?? provisioningInfo) : provisioningInfo)?.networkCorridorName} - ${(isCriticalLifecycle ? (testProvisioningInfo ?? provisioningInfo) : provisioningInfo)?.networkSiteName}`
                        : '—'}
                    </span>
                    <span className="text-muted-foreground">Date:</span>
                    <span>{formatDate((isCriticalLifecycle ? (testProvisioningInfo ?? provisioningInfo) : provisioningInfo)?.deviceProvisionRequestedDate)}</span>
                  </div>
                )}
                actionButton={
                  <>
                    {device.deviceStatus === 'testing' && renderActionButton('cancel-provision')}
                    {/* Provisioning success/failure is set by the prov service (ChirpStack)
                        callback via the API — not marked manually here. */}
                    {device.deviceStatus === 'testing' && renderActionButton('mark-as-tested')}
                    {device.deviceStatus === 'testing' && renderActionButton('mark-test-failed')}
                    {device.deviceStatus === 'testing' && (provisioningInfo?.deviceProvisioningStatus === 'failed' || provisioningInfo?.deviceProvisioningStatus === 'deprovisioned') && renderActionButton('retry-provision')}
                    {device.deviceStatus === 'testing' && provisioningInfo?.deviceProvisioningStatus === 'failed' && renderActionButton('deprovision')}
                  </>
                }
              />

              {/* Device Available / Tested */}
              <TimelineEvent
                title="Device Available for Activation"
                date={undefined}
                icon={CheckCircle}
                isCompleted={
                  isCriticalLifecycle ||
                  device.deviceStatus === 'tested' ||
                  device.deviceStatus === 'available' ||
                  device.deviceStatus === 'live'
                }
                isActive={device.deviceStatus === 'tested' || device.deviceStatus === 'available'}
                comments={(device.deviceStatus === 'tested' || device.deviceStatus === 'available' || isCriticalLifecycle) && (
                  <div className="space-y-1">
                    <p>Testing completed successfully</p>
                    {provisioningInfo?.deviceProvisioningStatus === 'deprovisioned' && provisioningInfo?.networkCorridorName && provisioningInfo?.networkSiteName && (
                      <p className="text-xs text-muted-foreground">
                        Previously tested at {provisioningInfo.networkCorridorName} - {provisioningInfo.networkSiteName}
                      </p>
                    )}
                    {device.deviceStatus === 'available' && (
                      <p className="text-xs text-muted-foreground">
                        Device is ready for live provisioning
                      </p>
                    )}
                  </div>
                )}
                actionButton={renderActionButton('activate-device')}
              />

              {/* Provisioned For Live Network */}
              {device.deviceStatus === 'live' && provisioningInfo?.deviceProvisioningType === 'live' && (
                <TimelineEvent
                  title={provisioningInfo?.deviceProvisioningStatus === 'failed' ? "Live Provisioning Failed" : "Provisioned For Live Network"}
                  date={formatNumber(provisioningInfo?.deviceProvisionRequestedDate) || undefined}
                  icon={Power}
                  isCompleted={
                    provisioningInfo?.deviceProvisioningStatus === 'success' ||
                    device.deviceStatus === 'live'
                  }
                  isActive={device.deviceStatus === 'live' && provisioningInfo?.deviceProvisioningStatus === 'requested'}
                  comments={formatNumber(provisioningInfo?.deviceProvisionRequestedDate) && (
                    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm mt-2">
                      <span className="text-muted-foreground">Status:</span>
                      <Badge
                        variant={
                          provisioningInfo?.deviceProvisioningStatus === 'success'
                            ? 'default'
                            : provisioningInfo?.deviceProvisioningStatus === 'failed'
                              ? 'destructive'
                              : 'secondary'
                        }
                        className="w-fit"
                      >
                        {provisioningInfo?.deviceProvisioningStatus
                          ? provisioningInfo.deviceProvisioningStatus.charAt(0).toUpperCase() + provisioningInfo.deviceProvisioningStatus.slice(1)
                          : '—'}
                      </Badge>
                      <span className="text-muted-foreground">Network:</span>
                      <span>
                        {provisioningInfo?.networkCorridorName && provisioningInfo?.networkSiteName
                          ? `${provisioningInfo.networkCorridorName} - ${provisioningInfo.networkSiteName}`
                          : '—'}
                      </span>
                      <span className="text-muted-foreground">Date:</span>
                      <span>{formatDate(provisioningInfo?.deviceProvisionRequestedDate)}</span>
                    </div>
                  )}
                />
              )}

              {/* Test Failed State */}
              {device.deviceStatus === 'failed' && (
                <TimelineEvent
                  title="Test Failed"
                  icon={AlertTriangle}
                  isActive={true}
                  isCompleted={true}
                  comments={
                    (procurementInfo as any)?.deviceTestFailureReason
                      ? `Reason: ${(procurementInfo as any).deviceTestFailureReason}`
                      : 'Device failed testing'
                  }
                  actionButton={
                    <>
                      {renderActionButton('mark-as-faulty')}
                      {renderActionButton('retire-device')}
                    </>
                  }
                />
              )}

              {/* Faulty State */}
              {device.deviceStatus === 'faulty' && (
                <TimelineEvent
                  title="Device Faulty"
                  date={formatNumber(faultInfo?.deviceFaultyDate) || undefined}
                  icon={AlertTriangle}
                  isActive={true}
                  isCompleted={true}
                  comments={formatNumber(faultInfo?.deviceFaultyDate) && `Marked faulty on ${formatDate(faultInfo?.deviceFaultyDate)}`}
                  actionButton={renderActionButton('retire-device')}
                />
              )}

              {/* Retired State */}
              {device.deviceStatus === 'retired' && (
                <TimelineEvent
                  title="Device Retired"
                  date={formatNumber(retirementInfo?.deviceRetirementDate) || undefined}
                  icon={Trash2}
                  isActive={true}
                  isCompleted={true}
                  comments="Device has been retired"
                />
              )}

            </div>
            </div>
          </ProfileSection>

        {/* Info Cards Section */}

          {/* Procurement Information */}
          <ProfileSection title="Procurement Information" icon={<Truck className="h-4 w-4" />} variant="dark">
            <div className="grid gap-4 p-5 md:grid-cols-3">
              <div>
                <p className="text-xs text-muted-foreground">Purchase Date</p>
                <p className={`font-medium ${!procurementInfo?.deviceOrderDate ? "text-muted-foreground/50" : ""}`}>{procurementInfo?.deviceOrderDate ? formatDate(procurementInfo.deviceOrderDate) : "Not purchased"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Shipped To</p>
                {(() => {
                  const addr = procurementInfo?.deviceShippedToAddress
                  const addrNumber = addr?.addressNumber ?? (procurementInfo as any)?.deviceShippedToAddressNumber
                  const formatted = addr ? formatAddress(addr as any) : null
                  if (formatted) return <p className="font-medium whitespace-pre-line">{formatted}</p>
                  if (addr?.addressName) return <p className="font-medium">{addr.addressName}</p>
                  if (addrNumber) return <p className="font-medium text-muted-foreground">Address #{addrNumber}</p>
                  return <p className="font-medium text-muted-foreground/50">No shipping address</p>
                })()}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Distributor</p>
                <p className={`font-medium ${!getDistributorDisplay() ? "text-muted-foreground/50" : ""}`}>{getDistributorDisplay() || "No distributor assigned"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Shipment Date</p>
                <p className={`font-medium ${!procurementInfo?.deviceShippingDate ? "text-muted-foreground/50" : ""}`}>{procurementInfo?.deviceShippingDate ? formatDate(procurementInfo.deviceShippingDate) : "Not shipped"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Purchase Order Number</p>
                <p className={`font-medium ${!procurementInfo?.devicePurchaseOrderNumber ? "text-muted-foreground/50" : ""}`}>{procurementInfo?.devicePurchaseOrderNumber || "No PO number"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Carrier</p>
                <p className={`font-medium ${!procurementInfo?.shipperName ? "text-muted-foreground/50" : ""}`}>{formatCarrierName(procurementInfo?.shipperName ?? undefined) || "No carrier details"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Receipt Date</p>
                <p className={`font-medium ${!procurementInfo?.deviceDeliveryDate ? "text-muted-foreground/50" : ""}`}>{procurementInfo?.deviceDeliveryDate ? formatDate(procurementInfo.deviceDeliveryDate) : "Not received"}</p>
              </div>
              {networkTechnology && (
                <div>
                  <p className="text-xs text-muted-foreground">Network Technology</p>
                  <p className="font-medium">{networkTechnology}</p>
                </div>
              )}
            </div>
          </ProfileSection>

          {/* Test Provisioning Information */}
          {testProvisioningInfo && (
            <ProfileSection title="Test Provisioning Information" icon={<Power className="h-4 w-4" />} variant="dark">
              <div className="grid gap-4 p-5 md:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground">Provisioning Status</p>
                  {testProvisioningInfo.deviceProvisioningStatus ? (
                    <Badge variant={testProvisioningInfo.deviceProvisioningStatus === 'success' ? 'default' : 'destructive'} className="mt-1">
                      {testProvisioningInfo.deviceProvisioningStatus.charAt(0).toUpperCase() + testProvisioningInfo.deviceProvisioningStatus.slice(1)}
                    </Badge>
                  ) : (
                    <p className="font-medium text-muted-foreground/50">Not provisioned</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Provisioning Type</p>
                  <p className="font-medium">
                    Test
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Provisioning Requested Date</p>
                  <p className={`font-medium ${!testProvisioningInfo.deviceProvisionRequestedDate ? "text-muted-foreground/50" : ""}`}>
                    {testProvisioningInfo.deviceProvisionRequestedDate ? formatDate(testProvisioningInfo.deviceProvisionRequestedDate) : "Not requested"}
                  </p>
                </div>
                {testProvisioningInfo.networkCorridorName && (
                  <div>
                    <p className="text-xs text-muted-foreground">Network Corridor</p>
                    <p className="font-medium">{testProvisioningInfo.networkCorridorName}</p>
                  </div>
                )}
                {testProvisioningInfo.networkSiteName && (
                  <div>
                    <p className="text-xs text-muted-foreground">Network Site</p>
                    <p className="font-medium">{testProvisioningInfo.networkSiteName}</p>
                  </div>
                )}
                {formatRequestedBy(testProvisioningInfo.deviceProvisionedRequestedBy) && (
                  <div>
                    <p className="text-xs text-muted-foreground">Requested By</p>
                    <p className="font-medium">{formatRequestedBy(testProvisioningInfo.deviceProvisionedRequestedBy)}</p>
                  </div>
                )}
              </div>
            </ProfileSection>
          )}

          {/* Live Provisioning Information */}
          {liveProvisioningInfo && (
            <ProfileSection title="Live Provisioning Information" icon={<Power className="h-4 w-4" />} variant="dark">
              <div className="grid gap-4 p-5 md:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground">Provisioning Status</p>
                  {liveProvisioningInfo.deviceProvisioningStatus ? (
                    <Badge variant={liveProvisioningInfo.deviceProvisioningStatus === 'success' ? 'default' : 'destructive'} className="mt-1">
                      {liveProvisioningInfo.deviceProvisioningStatus.charAt(0).toUpperCase() + liveProvisioningInfo.deviceProvisioningStatus.slice(1)}
                    </Badge>
                  ) : (
                    <p className="font-medium text-muted-foreground/50">Not provisioned</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Provisioning Type</p>
                  <p className="font-medium">
                    Live
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Provisioning Requested Date</p>
                  <p className={`font-medium ${!liveProvisioningInfo.deviceProvisionRequestedDate ? "text-muted-foreground/50" : ""}`}>
                    {liveProvisioningInfo.deviceProvisionRequestedDate ? formatDate(liveProvisioningInfo.deviceProvisionRequestedDate) : "Not requested"}
                  </p>
                </div>
                {liveProvisioningInfo.networkCorridorName && (
                  <div>
                    <p className="text-xs text-muted-foreground">Network Corridor</p>
                    <p className="font-medium">{liveProvisioningInfo.networkCorridorName}</p>
                  </div>
                )}
                {liveProvisioningInfo.networkSiteName && (
                  <div>
                    <p className="text-xs text-muted-foreground">Network Site</p>
                    <p className="font-medium">{liveProvisioningInfo.networkSiteName}</p>
                  </div>
                )}
                {formatRequestedBy(liveProvisioningInfo.deviceProvisionedRequestedBy) && (
                  <div>
                    <p className="text-xs text-muted-foreground">Requested By</p>
                    <p className="font-medium">{formatRequestedBy(liveProvisioningInfo.deviceProvisionedRequestedBy)}</p>
                  </div>
                )}
              </div>
            </ProfileSection>
          )}

          {/* Fault Information */}
          <ProfileSection
            title="Fault Information"
            icon={<AlertTriangle className="h-4 w-4" />}
            variant="dark"
            className={faultRecord ? "border-trilogy-red/20 bg-trilogy-red/5" : ""}
            action={
              canMarkFaulty ? (
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-7 gap-1.5"
                  onClick={() => setActiveActionId('mark-as-faulty')}
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Mark as Faulty
                </Button>
              ) : null
            }
          >
            <div className="grid gap-4 p-5 md:grid-cols-2">
              <div>
                <p className={`text-sm ${faultRecord ? "text-trilogy-red/80" : "text-muted-foreground"}`}>Fault Date</p>
                <p className={`font-medium ${faultRecord?.deviceFaultyDate ? "text-orange-900" : "text-muted-foreground/50"}`}>
                  {faultRecord?.deviceFaultyDate ? formatDate(faultRecord.deviceFaultyDate) : "No faults recorded"}
                </p>
              </div>
              <div>
                <p className={`text-sm ${faultRecord ? "text-trilogy-red/80" : "text-muted-foreground"}`}>Fault Notes</p>
                <p className={`text-sm ${faultRecord?.deviceFaultyNotes ? "text-orange-900" : "text-muted-foreground/50"}`}>
                  {faultRecord?.deviceFaultyNotes || "No fault notes available"}
                </p>
              </div>
            </div>
          </ProfileSection>

          {/* Retirement Information */}
          <ProfileSection
            title="Retirement Information"
            icon={<Trash2 className="h-4 w-4" />}
            variant="dark"
            className={retirementInfo ? "bg-slate-50" : ""}
            action={
              (['live', 'faulty', 'returned', 'failed'] as string[]).includes(device.deviceStatus) ? (
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-7 gap-1.5"
                  onClick={() =>
                    setActiveActionId(
                      device.deviceStatus === 'live' ? 'retire-device-from-live' : 'retire-device',
                    )
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Retire Device
                </Button>
              ) : null
            }
          >
            <div className="grid gap-4 p-5 md:grid-cols-2">
              <div>
                <p className={`text-sm ${retirementInfo ? "text-slate-600/80" : "text-muted-foreground"}`}>Device Retired Date</p>
                <p className={`font-medium ${retirementInfo?.deviceRetirementDate ? "text-slate-900" : "text-muted-foreground/50"}`}>
                  {retirementInfo?.deviceRetirementDate ? formatDate(retirementInfo.deviceRetirementDate) : "Not retired"}
                </p>
              </div>
              <div>
                <p className={`text-sm ${retirementInfo ? "text-slate-600/80" : "text-muted-foreground"}`}>Requested By</p>
                <p className={`font-medium ${formatRequestedBy(retirementRequestedBy) ? "text-slate-900" : "text-muted-foreground/50"}`}>
                  {formatRequestedBy(retirementRequestedBy) || "N/A"}
                </p>
              </div>
            </div>
          </ProfileSection>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
           {/* QR Code Card */}
           <ProfileSection title="Device QR Code" icon={<QrCode className="h-4 w-4" />} variant="dark">
             <div className="flex justify-center py-6">
               <QrCode className="h-48 w-48 text-foreground" />
             </div>
           </ProfileSection>

           {/* Device Image Card */}
           <ProfileSection title="Device Image" icon={<ImageIcon className="h-4 w-4" />} variant="dark">
             <div className="flex justify-center py-6">
               <div className="flex h-64 w-full items-center justify-center rounded-lg border bg-muted/10">
                 <ImageIcon className="h-24 w-24 text-muted-foreground/50" />
               </div>
             </div>
           </ProfileSection>
        </div>
      </div>

      {/* State-based Action Dialog */}
      {renderActionDialog()}

    </div>
  )
}
